import { Stack, Table, Text } from "@mantine/core";

import type { DayGroup } from "./macro-aggregations";
import { MACRO_FIELDS, type MacroTargetValues } from "./macro-aggregations";
import { MacroTargetCell } from "./MacroTargetCell";

const compactTableStyle = { fontSize: "0.8125rem" } as const;
const macroCellStyle = { verticalAlign: "top", minWidth: 170 } as const;

const createMacroHeaderCells = (prefix: string) =>
  MACRO_FIELDS.map((field) => (
    <Table.Th key={`${prefix}-${field.key}`} style={{ textAlign: "left" }}>
      {field.label}
    </Table.Th>
  ));

const createMacroValueCells = (totals: DayGroup["totals"], prefix: string, macroTargets?: MacroTargetValues) =>
  MACRO_FIELDS.map((field) => {
    const targetValue = macroTargets?.[field.key] ?? null;
    return (
      <Table.Td key={`${prefix}-${field.key}`} style={macroCellStyle}>
        <MacroTargetCell actualValue={totals[field.key]} targetValue={targetValue} targetLabel="daily target" />
      </Table.Td>
    );
  });

const createDailyHeaderRow = () => (
  <Table.Tr>
    <Table.Th>Day</Table.Th>
    <Table.Th>Meals logged</Table.Th>
    {createMacroHeaderCells("daily-header")}
  </Table.Tr>
);

const createDailyBodyRows = (dayGroups: DayGroup[], macroTargets?: MacroTargetValues) =>
  dayGroups.map((day) => (
    <Table.Tr key={day.key}>
      <Table.Td>
        <Text fw={600}>{day.label}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {day.meals.length} {day.meals.length === 1 ? "meal" : "meals"}
        </Text>
      </Table.Td>
      {createMacroValueCells(day.totals, `${day.key}-daily`, macroTargets)}
    </Table.Tr>
  ));

export const DailyBreakdownTable = ({
  dayGroups,
  macroTargets,
}: {
  dayGroups: DayGroup[];
  macroTargets?: MacroTargetValues;
}) => {
  const headerRow = createDailyHeaderRow();
  const bodyRows = createDailyBodyRows(dayGroups, macroTargets);

  return (
    <Stack gap={4}>
      <div style={{ overflowX: "auto", border: "1px solid #E5E7EB", borderRadius: 12 }}>
        <Table
          horizontalSpacing="sm"
          verticalSpacing="xs"
          striped
          highlightOnHover
          style={compactTableStyle}
        >
          <Table.Thead>{headerRow}</Table.Thead>
          <Table.Tbody>{bodyRows}</Table.Tbody>
        </Table>
      </div>
    </Stack>
  );
};
